import React, { useEffect, useState } from 'react';
import {
	Button,
	Container,
	Header,
	SpaceBetween,
	Box,
	Icon,
	FileUpload,
	Alert,
	Table,
	Modal,
	Link,
} from '@amzn/awsui-components-console';
import { BulkEntityValidationRecord } from 'src/components/selfServeWorkflows/requirementsPortal/bulkUploadSteps/BulkExecutionStepProgress';
import {
	TaskDefinition,
	WorkflowDefinition,
} from '@amzn/snow-nxt-workflow-service-typescript-client';

import AWS from 'aws-sdk';
import { v4 as uuidv4 } from 'uuid';
import { getAwsCredentials } from 'src/authentication/cognito';
import {
	BULK_UPLOAD_INPUT_BUCKET,
	SDS_APIGATEWAY_ENDPOINT,
	SNOW_VALIDATION_SERVICE_ENDPOINT,
	SNOW_DATA_SERVICE_ENDPOINT,
} from 'src/config';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import {
	getAttributeNames,
	getWorkflowTaskInputAttributes,
	buildEntityObject,
} from './BulkUploadUtils';
import { autoPopulateRows } from './bulkAutoPopulation';
import BulkAutoPopulationResultsModal from './BulkAutoPopulationResultsModal';
import CopyToClipboard from 'src/components/commonComponents/copyToClipboard/CopyToClipboard';
import {
	EXCEL_SHEET_CONTENT_TYPE,
	FileUploadStrings,
} from 'src/constants/BulkUploadConstants';
import { logger } from 'src/utils/logger';
import { ValidationExecutionStatus } from 'src/@types/validationTypes';
import { AUTO_GENERATED_ENTITY_IDENTIFIERS_WORKFLOW_IDS } from 'src/constants/CommonConstants';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';

interface BulkExecutionUploadInputStepProps {
	list: BulkEntityValidationRecord[];
	setList: React.Dispatch<React.SetStateAction<BulkEntityValidationRecord[]>>;
	fetchedRecords: BulkEntityValidationRecord[];
	setFetchedRecords: React.Dispatch<
		React.SetStateAction<BulkEntityValidationRecord[]>
	>;
	workflowDefinition: WorkflowDefinition | undefined;
	setBulkId: React.Dispatch<React.SetStateAction<string>>;
	taskDefinition: TaskDefinition[];
	bulkId: string;
	userName?: string;
}

const BulkExecutionUploadInputStep: React.FC<
	BulkExecutionUploadInputStepProps
> = (props) => {
	const [file, setFile] = useState<File>();
	const [validationBanner, setValidationBanner] = useState<boolean>(true);
	const [uploadFailedBanner, setUploadFailedBanner] = useState<boolean>(false);
	const [uploadErrorMessage, setUploadErrorMessage] = useState<string>('');
	const [errorHeader, setErrorHeader] = useState<string>('Upload Failed');
	const [fileUploadBanner, setFileUploadBanner] = useState<boolean>(false);
	const [uploadFileModalState, setUploadFileModalState] =
		useState<boolean>(false);
	const [tableLoadingInput, setTableLoadingInput] = useState({
		isLoading: false,
		loadingMessage: 'Loading resources',
	});
	const [uploadAPIResponse, setuploadAPIResponse] = useState({
		bulkExecutionId: '',
		bulKExecutionRecords: [],
	});
	const [hasValidatedOnce, setHasValidatedOnce] = useState<boolean>(false);
	const [autoPopulationModalRecord, setAutoPopulationModalRecord] =
		useState<BulkEntityValidationRecord | null>(null);
	const isAutoGeneratedEntityIdentifiersWorkflow =
		AUTO_GENERATED_ENTITY_IDENTIFIERS_WORKFLOW_IDS.includes(
			props.workflowDefinition?.workflowId as string
		);
	const defaultAttributes = ['EntityName', 'Region'];

	const FAILURE_STATUSES = new Set([
		ValidationExecutionStatus.VALIDATION_FAILED,
		ValidationExecutionStatus.VALIDATION_FATAL,
		ValidationExecutionStatus.INVALID,
		ValidationExecutionStatus.FAILED,
	]);

	const isFailureStatus = (status?: string): boolean =>
		FAILURE_STATUSES.has(status as ValidationExecutionStatus);

	const getDisplayErrorMessage = (rawError: string): string => {
		const genericMessages = ['internal server error', 'null'];

		try {
			const parsed = JSON.parse(rawError);
			const msg = parsed?.message || parsed?.error;

			if (!msg || genericMessages.includes(msg.toLowerCase())) {
				return '';
			}

			return msg;
		} catch {
			if (
				genericMessages.some((g) => rawError.toLowerCase().includes(g)) &&
				!rawError.toLowerCase().includes('column')
			) {
				return '';
			}

			return rawError;
		}
	};

	const hasValidationDefinitions = props.taskDefinition.some(
		(task) =>
			task.validationDefinitions && task.validationDefinitions.length > 0
	);

	const TERMINAL_VALIDATION_STATES = [
		ValidationExecutionStatus.VALIDATION_PASSED,
		ValidationExecutionStatus.VALID,
		ValidationExecutionStatus.VALIDATION_FAILED,
		ValidationExecutionStatus.VALIDATION_FATAL,
		ValidationExecutionStatus.INVALID,
		ValidationExecutionStatus.FAILED,
	];

	const areAllEntitiesValidated = (
		records: BulkEntityValidationRecord[]
	): boolean => {
		return records.every(
			(record) =>
				record.validationStatus &&
				TERMINAL_VALIDATION_STATES.includes(
					record.validationStatus as ValidationExecutionStatus
				)
		);
	};

	const isValidationPassed = (item: BulkEntityValidationRecord) =>
		item.validationStatus === ValidationExecutionStatus.VALIDATION_PASSED ||
		item.validationStatus === ValidationExecutionStatus.VALID;

	const allEntitiesValidated = areAllEntitiesValidated(props.fetchedRecords);

	useEffect(() => {
		if (props.bulkId && props.bulkId.trim() !== '') {
			const url = new URL(window.location.href);

			url.searchParams.set('bulkExecutionId', props.bulkId);

			const hashParts = url.hash.split('?');
			const hashPath = hashParts[0];
			const hashParams = new URLSearchParams(hashParts[1] || '');

			hashParams.set('bulkExecutionId', props.bulkId);
			url.hash = `${hashPath}?${hashParams.toString()}`;

			window.history.replaceState({}, '', url.toString());

			if (uploadAPIResponse.bulkExecutionId === '') {
				handleFetchRecordsClick();
			}
		}
	}, [props.bulkId]);

	useEffect(() => {
		if (
			props.fetchedRecords.length === 0 ||
			areAllEntitiesValidated(props.fetchedRecords) ||
			tableLoadingInput.isLoading ||
			!hasValidatedOnce ||
			uploadFailedBanner
		) {
			return;
		}

		const pollingInterval = setInterval(handleFetchRecordsClick, 15000);

		return () => clearInterval(pollingInterval);
	}, [props.fetchedRecords, tableLoadingInput.isLoading, hasValidatedOnce]);

	const handleFetchRecordsClick = async () => {
		setTableLoadingInput({
			isLoading: true,
			loadingMessage: 'Loading resources',
		});

		const bulkExecutionIdToUse =
			props.bulkId ||
			uploadAPIResponse.bulkExecutionId ||
			(props.fetchedRecords.length > 0
				? props.fetchedRecords[0]?.bulkExecutionId
				: '');

		if (!bulkExecutionIdToUse) {
			logger.warn('No bulkExecutionId available for fetching records');
			setTableLoadingInput({
				isLoading: false,
				loadingMessage: 'Loading resources',
			});

			return;
		}

		const apiPath = hasValidationDefinitions
			? '/bulk/validation/results'
			: '/bulk/fetch';
		const apiUrl = hasValidationDefinitions
			? SNOW_VALIDATION_SERVICE_ENDPOINT
			: SDS_APIGATEWAY_ENDPOINT;

		const fetchPayloadRequest = hasValidationDefinitions
			? {
					bulkExecutionId: bulkExecutionIdToUse,
					workflowId: props.workflowDefinition?.workflowId,
			  }
			: {
					bulkExecutionId: bulkExecutionIdToUse,
					filter: 'BULK_EXECUTION_ID',
					username: props.userName,
					workflowId: props.workflowDefinition?.workflowId,
			  };

		const apiResponse = await callSNOWDataService({
			path: apiPath,
			method: 'GET',
			url: apiUrl,
			queryParams: fetchPayloadRequest,
		} as Parameters<typeof callSNOWDataService>[0]).catch((err) => {
			setTableLoadingInput({
				isLoading: false,
				loadingMessage: 'Loading resources',
			});
			setUploadFailedBanner(true);
			setErrorHeader('Fetch Records Failed');
			setUploadErrorMessage(getDisplayErrorMessage(err.message || ''));
			logger.error(err.message);

			return null;
		});

		const resultsField = hasValidationDefinitions
			? 'validationResults'
			: 'bulkExecutionRecords';

		if (!apiResponse || !apiResponse[resultsField]) {
			logger.warn(`No ${resultsField} returned from fetch API`);
			setTableLoadingInput({
				isLoading: false,
				loadingMessage: 'Loading resources',
			});

			return;
		}

		const fetchedRecords: BulkEntityValidationRecord[] = apiResponse[
			resultsField
		]
			.map((record) => {
				let entityName;
				let validationStatus;
				let inputRecord;

				if (hasValidationDefinitions) {
					entityName = record.EntityName || record.entityName;
					validationStatus = record.status || '';
					inputRecord = record.inputRecord || {};
				} else {
					entityName =
						record.entityId || record.EntityName || record.entityName;
					validationStatus = record.validationStatus || '';
					inputRecord = record.bulkUploadRecords || record.inputRecord || {};
				}

				if (!entityName || entityName === 'null') return null;

				return {
					entityId: entityName,
					entityType: props.workflowDefinition?.entityType || 'fc',
					workflowId: props.workflowDefinition?.workflowId || '',
					bulkExecutionId: bulkExecutionIdToUse,
					validationStatus,
					validationExecutionId: record.validationExecutionId || '',
					validationResults: record.validationResults || null,
					validationReport: record.validationReport || null,
					validationFailureMessage:
						record.validationFailureReason ||
						record.validationFailureMessage ||
						'',
					inputRecord,
					autoPopulated: record.AutoPopulated,
					autoPopulationResults:
						record.AutoPopulationResults ||
						record.autoPopulationResults ||
						undefined,
				};
			})
			.filter((record) => record !== null);

		props.setFetchedRecords(
			mergeValidatedAndPendingRecords(props.fetchedRecords, fetchedRecords)
		);

		setTableLoadingInput({
			isLoading: false,
			loadingMessage: 'Loading resources',
		});
	};

	const mergeValidatedAndPendingRecords = (
		recordsToBeValidated: BulkEntityValidationRecord[],
		recordsValidated: BulkEntityValidationRecord[]
	) => {
		const entityIdToRecord = new Map<string, BulkEntityValidationRecord>();

		recordsToBeValidated.forEach((record) => {
			entityIdToRecord.set(record.entityId, record);
		});

		recordsValidated.forEach((record) => {
			const existing = entityIdToRecord.get(record.entityId);

			if (existing) {
				entityIdToRecord.set(record.entityId, {
					...existing,
					validationStatus: record.validationStatus,
					validationExecutionId: record.validationExecutionId,
					validationResults: record.validationResults,
					validationReport: record.validationReport,
					validationFailureMessage: record.validationFailureMessage,
				});
			} else {
				entityIdToRecord.set(record.entityId, record);
			}
		});

		return Array.from(entityIdToRecord.values()).sort((a, b) => {
			const getPriority = (status: string | undefined): number => {
				if (
					status === ValidationExecutionStatus.VALIDATION_PASSED ||
					status === ValidationExecutionStatus.VALID
				)
					return 2;
				if (isFailureStatus(status)) return 0;

				return 1;
			};

			return getPriority(a.validationStatus) - getPriority(b.validationStatus);
		});
	};

	const handleFileUpload = async () => {
		setFileUploadBanner(true);
	};

	const getValidationStatusIcon = (status: string | undefined) => {
		if (
			status === ValidationExecutionStatus.VALIDATION_PASSED ||
			status === ValidationExecutionStatus.VALID
		) {
			return (
				<SpaceBetween direction={'horizontal'} size={'s'}>
					<Icon name="status-positive" variant="success" />
					<p>Valid</p>
				</SpaceBetween>
			);
		} else if (isFailureStatus(status)) {
			return (
				<SpaceBetween direction={'horizontal'} size={'s'}>
					<Icon name="status-negative" variant="error" />
					<p>Invalid</p>
				</SpaceBetween>
			);
		} else {
			return (
				<SpaceBetween direction={'horizontal'} size={'s'}>
					<Icon name="status-pending" variant="normal" />
					<p>Pending</p>
				</SpaceBetween>
			);
		}
	};

	useEffect(() => {
		const uploadFileToS3AndCallService = async () => {
			if (!fileUploadBanner) {
				return;
			}

			setTableLoadingInput({
				isLoading: true,
				loadingMessage: 'Uploading File',
			});
			setUploadFileModalState(false);

			await getAwsCredentials();

			const s3 = new AWS.S3();
			const params = {
				Bucket: BULK_UPLOAD_INPUT_BUCKET,
				Key: `input/${props.workflowDefinition?.workflowId}/${file?.name}`,
				Expires: 60,
				ContentType: EXCEL_SHEET_CONTENT_TYPE,
			};

			const url = await s3.getSignedUrlPromise('putObject', params);

			await fetch(url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': EXCEL_SHEET_CONTENT_TYPE,
				},
			})
				.then(() => setUploadFileModalState(false))
				.catch((err) => {
					logger.error(err);
					setTableLoadingInput({
						isLoading: false,
						loadingMessage: '',
					});
				});

			const uploadPayload = {
				workflowId: props.workflowDefinition?.workflowId,
				domain: 'SNOW',
				fileName: `${file?.name}`,
				bulkExecutionId: new Date().getTime().toString(),
				expectedAttributeKeys: Array.from(
					new Set([
						...getAttributeNames(props.taskDefinition),
						...(isAutoGeneratedEntityIdentifiersWorkflow
							? []
							: defaultAttributes),
					])
				),
			};

			setTableLoadingInput({
				isLoading: true,
				loadingMessage: 'Parsing records',
			});
			const apiResponse = await callSNOWDataService({
				path: '/bulk/upload',
				method: 'POST',
				url: SDS_APIGATEWAY_ENDPOINT,
				body: uploadPayload,
				queryParams: {},
			} as Parameters<typeof callSNOWDataService>[0]).catch((err) => {
				setUploadFailedBanner(true);
				setErrorHeader('Upload Failed');
				setUploadErrorMessage(getDisplayErrorMessage(err.message || ''));
				setFileUploadBanner(false);
				setTableLoadingInput({
					isLoading: false,
					loadingMessage: '',
				});
				logger.error(err.message);

				return null;
			});

			if (!apiResponse) return;

			if (
				!apiResponse.bulkExecutionRecords ||
				apiResponse.bulkExecutionRecords.length === 0
			) {
				setUploadFailedBanner(true);
				setErrorHeader('Upload Failed');
				setUploadErrorMessage(
					'No records found in the uploaded file. Please check the file and try again.'
				);
				setFileUploadBanner(false);
				setTableLoadingInput({
					isLoading: false,
					loadingMessage: '',
				});

				return;
			}

			setuploadAPIResponse({
				bulkExecutionId: apiResponse.bulkExecutionId,
				bulKExecutionRecords: apiResponse.bulkExecutionRecords,
			});
			props.setBulkId(apiResponse.bulkExecutionId);

			const parsedRecords: BulkEntityValidationRecord[] =
				apiResponse.bulkExecutionRecords.map((record) => {
					const entityIdentifier = isAutoGeneratedEntityIdentifiersWorkflow
						? uuidv4().toString()
						: record.EntityName;

					return {
						entityId: entityIdentifier,
						EntityName: entityIdentifier,
						entityType: props.workflowDefinition?.entityType,
						bulkExecutionId: apiResponse.bulkExecutionId,
						inputRecord: record,
					};
				});

			setTableLoadingInput({
				isLoading: true,
				loadingMessage: 'Auto-populating attributes',
			});
			const workflowId = props.workflowDefinition?.workflowId as string;
			const enrichedRecords = await autoPopulateRows(parsedRecords, workflowId);

			// Create entities for ALL parsed records
			await createEntitiesForAllRecords(
				enrichedRecords,
				apiResponse.bulkExecutionId
			);
		};

		if (file) {
			uploadFileToS3AndCallService();
		}
	}, [fileUploadBanner]);

	const validateRecords = async (
		recordsToValidate?: BulkEntityValidationRecord[]
	) => {
		setTableLoadingInput({
			isLoading: true,
			loadingMessage: 'Validating entities',
		});

		const recordsForValidation = recordsToValidate || props.fetchedRecords;

		if (recordsForValidation.length === 0) {
			logger.warn('No records to validate');
			setTableLoadingInput({
				isLoading: false,
				loadingMessage: '',
			});

			return;
		}

		const transformedRecords = recordsForValidation.map((record) => {
			const { EntityName: inputEntityName, ...restInputRecord } =
				record.inputRecord || {};

			return {
				entityId: record.entityId,
				EntityName: (inputEntityName as string) || record.entityId,
				entityType: record.entityType,
				bulkExecutionId: record.bulkExecutionId,
				...restInputRecord,
			};
		});

		const bulkExecutionIdToUse =
			recordsForValidation[0]?.bulkExecutionId ||
			props.bulkId ||
			uploadAPIResponse.bulkExecutionId;

		if (!bulkExecutionIdToUse) {
			logger.error('No bulkExecutionId available for validation');
			setTableLoadingInput({
				isLoading: false,
				loadingMessage: '',
			});

			return;
		}

		const apiPath = hasValidationDefinitions
			? '/validation/bulk'
			: '/bulk/validate';
		const apiUrl = hasValidationDefinitions
			? SNOW_VALIDATION_SERVICE_ENDPOINT
			: SDS_APIGATEWAY_ENDPOINT;

		const validatePayload = {
			workflowId: props.workflowDefinition?.workflowId,
			entityType: props.workflowDefinition?.entityType,
			bulkExecutionId: bulkExecutionIdToUse,
			bulkExecutionRecords: transformedRecords,
			workflowTaskInputs: getWorkflowTaskInputAttributes(props.taskDefinition),
			fileName: `${file?.name}`,
			username: props.userName,
		};

		logger.info('Bulk validation request payload:', validatePayload);

		await callSNOWDataService({
			path: apiPath,
			method: 'POST',
			url: apiUrl,
			body: validatePayload,
			queryParams: {},
		} as Parameters<typeof callSNOWDataService>[0]).catch((err) => {
			logger.error(err.message);
			setUploadFailedBanner(true);
			setErrorHeader('Validation Failed');
			setUploadErrorMessage(getDisplayErrorMessage(err.message || ''));
			setTableLoadingInput({
				isLoading: false,
				loadingMessage: '',
			});
		});

		setValidationBanner(false);
		setHasValidatedOnce(true);
		handleFetchRecordsClick();
	};

	const createEntitiesForAllRecords = async (
		records: BulkEntityValidationRecord[],
		bulkExecutionId: string
	) => {
		setTableLoadingInput({
			isLoading: true,
			loadingMessage: 'Creating entities',
		});

		const entitiesToCreate = records.map((record) =>
			buildEntityObject(
				props.taskDefinition,
				record.inputRecord,
				props.workflowDefinition
			)
		);

		await getAwsCredentials();
		const credentials = {
			accessKey: AWS.config.credentials?.accessKeyId,
			secretKey: AWS.config.credentials?.secretAccessKey,
			sessionToken: AWS.config.credentials?.sessionToken,
		};

		const createdEntities: BulkEntityValidationRecord[] = [];

		try {
			await Promise.all(
				entitiesToCreate.map(async (entity, index) => {
					const creationTime = new Date(Date.now()).toISOString();
					const typedEntity = entity as {
						EntityName?: string;
						entityName?: string;
					};
					const entityName = typedEntity.EntityName || typedEntity.entityName;
					const sourceRecord =
						records.find((r) => r.entityId === entityName) || records[index];
					const requestEntity = {
						...entity,
						CreatedBy: props.userName!,
						LastStatusUpdateTime: creationTime,
						CreationTime: creationTime,
						LastStatusUpdatedBy: props.userName!,
						WorkflowType: props.workflowDefinition?.workflowId,
						Status: 'DRAFT',
						bulkExecutionId,
						AutoPopulated: sourceRecord.autoPopulated === true,
						AutoPopulationResults: sourceRecord.autoPopulationResults || {},
						EntityAttributes: {
							...((entity as Record<string, unknown>)
								.EntityAttributes as Record<string, unknown>),
							approvalRequiredToStartExecution: props.workflowDefinition
								?.approvalRequiredToStartExecution
								? 'true'
								: 'false',
							isReRunAllowed: (
								props.workflowDefinition as unknown as Record<string, unknown>
							)?.isReRunAllowed
								? 'true'
								: 'false',
						},
					} as Record<string, unknown> & {
						EntityName?: string;
						entityName?: string;
					};

					await callSNOWDataService({
						path: '/entity',
						method: 'POST',
						url: SNOW_DATA_SERVICE_ENDPOINT,
						body: {
							entity: requestEntity,
							requestId: uuidv4(),
						},
						credentials,
					} as Parameters<typeof callSNOWDataService>[0]);

					createdEntities.push({
						...records[index],
						entityId: (requestEntity.EntityName ||
							requestEntity.entityName ||
							records[index].entityId) as string,
					});
				})
			);

			props.setFetchedRecords(createdEntities);

			setTableLoadingInput({
				isLoading: false,
				loadingMessage: '',
			});

			await validateRecords(createdEntities);
		} catch (err) {
			logger.error('Error creating entities:', err as Error);
			setUploadFailedBanner(true);
			setErrorHeader('Entity Creation Failed');
			setUploadErrorMessage(
				getDisplayErrorMessage((err as Error).message || '')
			);
			setTableLoadingInput({
				isLoading: false,
				loadingMessage: '',
			});
		}
	};

	const handleUploadFileClick = () => {
		setUploadFileModalState(!uploadFileModalState);
		setUploadFailedBanner(false);
		setUploadErrorMessage('');
	};

	return (
		<Container
			header={
				<Header variant="h2">
					Workflow Type: {props.workflowDefinition?.workflowDefinitionName}
				</Header>
			}
		>
			<SpaceBetween size="s">
				{uploadFailedBanner && (
					<Alert
						dismissible
						onDismiss={() => {
							setUploadFailedBanner(false);
							setUploadErrorMessage('');
							setErrorHeader('Upload Failed');
						}}
						statusIconAriaLabel="error"
						header={errorHeader}
						type="error"
					>
						{uploadErrorMessage ||
							'An error occurred. Please try again or contact snow-dev@ for support.'}
					</Alert>
				)}
				{validationBanner && props.fetchedRecords.length === 0 && (
					<Alert
						dismissible
						onDismiss={() => setValidationBanner(false)}
						statusIconAriaLabel="Info"
						header="Validation"
					>
						Please Upload your file, validate it, then select validated entities
						to continue.
					</Alert>
				)}
				{hasValidatedOnce &&
					!areAllEntitiesValidated &&
					props.fetchedRecords.length > 0 && (
						<Alert
							statusIconAriaLabel="Info"
							header="Validation In Progress"
							type="info"
						>
							Validation is in progress. Entity selection will be enabled once
							all entities have completed validation.
						</Alert>
					)}
				{allEntitiesValidated &&
					props.fetchedRecords.some((r) =>
						isFailureStatus(r.validationStatus)
					) && (
						<Alert
							statusIconAriaLabel="Warning"
							header="Validation Failed"
							type="warning"
						>
							Some entities failed validation. Please review the validation
							results below. Only valid entities can be selected for execution.
						</Alert>
					)}
				{props.bulkId !== '' && (
					<Alert
						statusIconAriaLabel="info"
						type="warning"
						action={
							<CopyToClipboard
								copyText={window.location.href}
								copyButtonLabel={'copy link'}
								successText={'copied!'}
								errorText={'error'}
								variant={'normal'}
							/>
						}
					>
						Copy the URL to securely return to this page and complete your
						workflow at your convenience.
					</Alert>
				)}
				<Box>
					<Table
						renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
							`Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
						}
						onSelectionChange={({ detail }) => {
							props.setList(
								detail.selectedItems as BulkEntityValidationRecord[]
							);
						}}
						selectedItems={props.list}
						ariaLabels={{
							selectionGroupLabel: 'Items selection',
							allItemsSelectionLabel: ({ selectedItems }) =>
								`${selectedItems.length} ${
									selectedItems.length === 1 ? 'item' : 'items'
								} selected`,
						}}
						stickyColumns={{ first: 1, last: 0 }}
						resizableColumns={true}
						columnDefinitions={[
							{
								id: 'entityId',
								header: 'Entity Id',
								cell: (record: BulkEntityValidationRecord) => record.entityId,
								sortingField: 'entityId',
								isRowHeader: true,
							},
							{
								id: 'validation',
								header: 'Validation Status',
								cell: (record: BulkEntityValidationRecord) =>
									getValidationStatusIcon(record.validationStatus),
							},
							{
								id: 'autoPopulationResults',
								header: 'Auto-Population Results',
								cell: (record: BulkEntityValidationRecord) => {
									const results = record.autoPopulationResults || {};
									const count = Object.keys(results).length;

									if (count === 0) return '-';

									return (
										<Link
											onFollow={(e) => {
												e.preventDefault();
												setAutoPopulationModalRecord(record);
											}}
										>
											View results ({count})
										</Link>
									);
								},
							},
							{
								id: 'validationResults',
								header: 'Validation Results',
								cell: (record: BulkEntityValidationRecord) => {
									if (
										!record.validationResults ||
										(Array.isArray(record.validationResults) &&
											record.validationResults.length === 0)
									) {
										return record.validationFailureMessage || '-';
									}

									const results = Array.isArray(record.validationResults)
										? record.validationResults
										: [];

									return (
										<Box>
											<Table
												columnDefinitions={[
													{
														id: 'validationName',
														header: 'Validation Name',
														cell: (item: { validationName?: string }) =>
															item.validationName || '-',
													},
													{
														id: 'validationId',
														header: 'Validation ID',
														cell: (item: { validationId?: string }) =>
															item.validationId || '-',
													},
													{
														id: 'status',
														header: 'Status',
														cell: (item: { status?: string }) =>
															item.status || '-',
													},
													{
														id: 'errorMessage',
														header: 'Error Message',
														cell: (item: { errorMessage?: string }) =>
															item.errorMessage || '-',
													},
												]}
												items={results}
												variant="embedded"
												empty={
													<Box textAlign="center" color="inherit">
														<b>No validation results</b>
													</Box>
												}
											/>
										</Box>
									);
								},
								width: 600,
							},
						]}
						columnDisplay={[
							{ id: 'entityId', visible: true },
							{ id: 'entityType', visible: true },
							{ id: 'validation', visible: true },
							{ id: 'autoPopulationResults', visible: true },
							{ id: 'validationResults', visible: true },
						]}
						loading={tableLoadingInput.isLoading}
						loadingText={tableLoadingInput.loadingMessage}
						selectionType="multi"
						trackBy="entityId"
						isItemDisabled={(item: BulkEntityValidationRecord) =>
							!allEntitiesValidated || !isValidationPassed(item)
						}
						empty={
							<Box
								margin={{ vertical: 'xs' }}
								textAlign="center"
								color="inherit"
							>
								<SpaceBetween size="m">
									<b>No records</b>
								</SpaceBetween>
							</Box>
						}
						header={
							<Header
								counter={`(${props.fetchedRecords.length})`}
								actions={
									<SpaceBetween direction="horizontal" size="xs">
										<Button
											onClick={handleFetchRecordsClick}
											iconName={'refresh'}
										/>
										<Button
											variant="primary"
											onClick={handleUploadFileClick}
											disabled={props.bulkId !== ''}
										>
											Upload File
										</Button>
									</SpaceBetween>
								}
							>
								Requests
							</Header>
						}
						items={props.fetchedRecords}
						wrapLines
						stripedRows={getDefaultStripedRows()}
					/>
				</Box>
			</SpaceBetween>
			<Modal visible={uploadFileModalState} onDismiss={handleUploadFileClick}>
				<SpaceBetween size={'s'}>
					<Alert type="info" header="Instructions">
						<p>
							Upload an Excel file containing the requests for which you want to
							start the workflow executions.
						</p>
					</Alert>
					<Box float="left">
						<SpaceBetween size={'s'} direction="horizontal">
							<Button
								variant="primary"
								onClick={handleFileUpload}
								disabled={!file}
							>
								Upload
							</Button>
							<FileUpload
								onChange={async ({ detail }) => {
									setFile(detail.value[0]);
								}}
								value={file === undefined ? [] : [file]}
								accept={'.xlsx'}
								i18nStrings={{
									uploadButtonText: () => FileUploadStrings.UPLOAD_BUTTON_TEXT,
									dropzoneText: () => FileUploadStrings.DROPZONE_TEXT,
									removeFileAriaLabel: () =>
										FileUploadStrings.REMOVE_FILE_ARIA_LABEL,
									limitShowFewer: FileUploadStrings.LIMIT_SHOW_FEWER,
									limitShowMore: FileUploadStrings.LIMIT_SHOW_MORE,
									errorIconAriaLabel: FileUploadStrings.ERROR_ICON_ARIA_LABEL,
								}}
								showFileLastModified
								showFileSize
								showFileThumbnail
								multiple={false}
								tokenLimit={1}
								constraintText={FileUploadStrings.CONSTRAINT_TEXT}
							/>
						</SpaceBetween>
					</Box>
				</SpaceBetween>
			</Modal>
			<BulkAutoPopulationResultsModal
				record={autoPopulationModalRecord}
				onDismiss={() => setAutoPopulationModalRecord(null)}
			/>
		</Container>
	);
};

export default BulkExecutionUploadInputStep;
